import type { Job, JobStatus, JobPriority, JobTypeOnJob, ServiceCallFeeState } from '@/types/job';

export function normalizeAuthorization(raw: unknown): Job['authorization'] {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const record = raw as Record<string, unknown>;
  return {
    ...(record as NonNullable<Job['authorization']>),
    collectedAt:
      record.collectedAt != null
        ? String(record.collectedAt)
        : record.authorizedAt != null
          ? String(record.authorizedAt)
          : null,
  };
}

export function normalizeSignoff(raw: unknown): Job['signoff'] {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const record = raw as Record<string, unknown>;
  return {
    ...(record as NonNullable<Job['signoff']>),
    collectedAt:
      record.collectedAt != null
        ? String(record.collectedAt)
        : record.signedAt != null
          ? String(record.signedAt)
          : null,
  };
}

// Service-call fee lifecycle (SPEC-service-call-lifecycle D3) — Json column
// passthrough. `undefined` (older backend omits the field) and `null` (no fee)
// are both meaningful to display code, so keep them distinct instead of
// defaulting.
export function normalizeServiceCallFee(raw: unknown): Job['serviceCallFee'] {
  if (raw === undefined) return undefined;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const record = raw as Record<string, unknown>;
  const appliedTo =
    record.appliedTo && typeof record.appliedTo === 'object' && !Array.isArray(record.appliedTo)
      ? {
          type: String((record.appliedTo as Record<string, unknown>).type ?? 'JOB') as NonNullable<
            NonNullable<Job['serviceCallFee']>['appliedTo']
          >['type'],
          id: String((record.appliedTo as Record<string, unknown>).id ?? ''),
        }
      : null;
  return {
    amount: Number(record.amount ?? 0),
    policy: String(record.policy ?? 'WAIVE_ON_CALL') as ServiceCallFeeState['policy'],
    status: String(record.status ?? 'PENDING') as ServiceCallFeeState['status'],
    appliedTo,
    appliedAt: record.appliedAt != null ? String(record.appliedAt) : null,
    creditMemoId: record.creditMemoId != null ? String(record.creditMemoId) : null,
  };
}

function nestedId(raw: unknown) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const record = raw as Record<string, unknown>;
  return record.id != null ? String(record.id) : null;
}

function normalizeJobType(raw: unknown, jobTypeId?: unknown): JobTypeOnJob {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const record = raw as Record<string, unknown>;
    const skills = Array.isArray(record.requiredSkills) ? record.requiredSkills : [];
    return {
      id: record.id != null ? String(record.id) : null,
      name: String(record.name || 'Unassigned'),
      shortCode: String(record.shortCode || 'UNASSIGNED'),
      color: record.color != null ? String(record.color) : undefined,
      defaultDuration: record.defaultDuration != null ? Number(record.defaultDuration) : undefined,
      minCrewSize: record.minCrewSize != null ? Number(record.minCrewSize) : undefined,
      requiredSkills: skills.map((skill) => {
        const value = skill as Record<string, unknown>;
        return {
          id: String(value.id ?? ''),
          name: String(value.name ?? ''),
          slug: String(value.slug ?? ''),
          durationMinutes: Number(value.durationMinutes ?? 0),
          category: String(value.category ?? 'GENERAL'),
          color: value.color != null ? String(value.color) : undefined,
        };
      }),
    };
  }

  // No linked job type — the job is untyped. "Unassigned" is a display fallback
  // only; the job carries no type label of its own.
  const name = String(raw || 'Unassigned');
  return {
    id: jobTypeId != null ? String(jobTypeId) : null,
    name,
    shortCode: name.slice(0, 10).toUpperCase(),
    requiredSkills: [],
  };
}

export function normalizeJob(raw: Record<string, unknown>): Job {
  return {
    ...raw,
    id: String(raw.id ?? raw._id ?? ''),
    tenantId: String(raw.tenantId ?? ''),
    jobNumber: String(raw.jobNumber ?? ''),
    status: String(raw.status ?? 'PENDING') as JobStatus,
    // Detail returns `jobType` as an object; the list returns the row with a
    // `jobTypeRef` relation. Either way the name comes from the catalog — the
    // job itself no longer stores a type label.
    jobType: normalizeJobType(raw.jobType ?? raw.jobTypeRef, raw.jobTypeId),
    name: String(raw.name ?? ''),
    priority: String(raw.priority ?? 'NORMAL') as JobPriority,
    source: raw.source != null ? String(raw.source) : null,
    // NOT-560 — left undefined (not false) when the backend omits it so the
    // dual-read helper can still fall back to the legacy source string.
    scopeMode:
      raw.scopeMode === 'DIAGNOSTIC'
        ? 'DIAGNOSTIC'
        : raw.scopeMode === 'DEFINED'
          ? 'DEFINED'
          : undefined,
    isServiceCall: raw.isServiceCall != null ? Boolean(raw.isServiceCall) : undefined,
    serviceCallFee: normalizeServiceCallFee(raw.serviceCallFee),
    contactId: raw.contactId != null ? String(raw.contactId) : nestedId(raw.contact),
    estimateId: raw.estimateId != null ? String(raw.estimateId) : null,
    bookingId: raw.bookingId != null ? String(raw.bookingId) : null,
    lineItems: (raw.lineItems ?? []) as Array<Record<string, unknown>>,
    serviceAddress: raw.serviceAddress as Job['serviceAddress'],
    subtotal: Number(raw.subtotal ?? 0),
    taxTotal: Number(raw.taxTotal ?? 0),
    discountTotal: Number(raw.discountTotal ?? 0),
    grandTotal: Number(raw.grandTotal ?? 0),
    assignedTo: raw.assignedTo != null ? String(raw.assignedTo) : null,
    scheduledStart: raw.scheduledStart != null ? String(raw.scheduledStart) : null,
    scheduledEnd: raw.scheduledEnd != null ? String(raw.scheduledEnd) : null,
    completedAt: raw.completedAt != null ? String(raw.completedAt) : null,
    cancelledAt: raw.cancelledAt != null ? String(raw.cancelledAt) : null,
    archivedAt: raw.archivedAt != null ? String(raw.archivedAt) : null,
    pipelineId: raw.pipelineId != null ? String(raw.pipelineId) : null,
    stageId: raw.stageId != null ? String(raw.stageId) : null,
    pausedFromStageId: raw.pausedFromStageId != null ? String(raw.pausedFromStageId) : null,
    statusPhase: raw.statusPhase != null ? String(raw.statusPhase) : null,
    authorization: normalizeAuthorization(raw.authorization),
    signoff: normalizeSignoff(raw.signoff),
    createdAt: String(raw.createdAt ?? new Date().toISOString()),
    updatedAt: String(raw.updatedAt ?? new Date().toISOString()),
  } as Job;
}
